/**
 * A rule's window line: "measured over" the formula's first range (inherited)
 * or the rule's own with × back — or, when the rule reads no metric, why it
 * needs no window (the score ranking, or the items' tags judged on Tag Rules).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import {
  DROP_GEN_FIRST_RANGE,
  DROP_GEN_HOUR_DATE_RANGES,
  type DropGenHourFormulaGroup,
  type DropGenRule,
  hourRangeKey,
  hourRangeLabel,
  readsMetrics,
  usesRanking,
  usesTags,
} from '@trafficpulse/shared';

import { NoWindowNote, RuleRangeChoice } from '@/features/flow-optimizer/tabs/exclude/group/range/RuleRangeChoice';

import { RULE_SURFACES, type RuleSurface } from '../rule-surfaces';

export type FirstRange = Pick<DropGenHourFormulaGroup, 'dateRange' | 'dateRangeCustomFrom' | 'dateRangeCustomTo'>;

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "measured over [Last 14 days · inherited]" — the formula's first range — or the rule's own with × back. */
export const MeasuredOver: FC<{ rule: DropGenRule; surface: RuleSurface; firstRange?: FirstRange; onChange: (patch: Partial<DropGenRule>) => void }> = ({ rule, surface, firstRange, onChange }) => {
  const { item, tags } = RULE_SURFACES[surface];
  if (!readsMetrics(rule.when) && usesTags(rule.when) && !usesRanking(rule.when))
    return <NoWindowNote text={`No date window needed — each ${item} tag judges over its own range`} title={`A ${item} tag is judged on ${tags.page}, over its own date range. Add a metric condition to measure one here.`} />;
  if (!readsMetrics(rule.when))
    return <NoWindowNote text="No date window needed — the score is read from the ranking" title="A score-ranking condition reads each item's place in the formula's ranking, not a metric window. Add a metric condition to measure one." />;
  if (surface === 'routeTest')
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12.5px] font-semibold text-muted-foreground">measured over</span>
        <span className="text-xs font-medium" title="A route test only reads today">Today</span>
      </div>
    );
  const label = firstRange ? capital(hourRangeLabel(hourRangeKey(firstRange))) : 'The formula’s first range';
  const seed = firstRange ?? { dateRange: 'last-7-days' };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[12.5px] font-semibold text-muted-foreground">measured over</span>
      <RuleRangeChoice
        pinned={rule.dateRange === DROP_GEN_FIRST_RANGE ? null : { dateRange: rule.dateRange, dateRangeCustomFrom: rule.dateRangeCustomFrom, dateRangeCustomTo: rule.dateRangeCustomTo }}
        inheritedLabel={label}
        inheritedTitle={`The formula's first range (${label}) — it follows the formula. Click only to give this rule its own.`}
        presets={DROP_GEN_HOUR_DATE_RANGES}
        onPin={() => onChange({ dateRange: seed.dateRange, dateRangeCustomFrom: seed.dateRangeCustomFrom, dateRangeCustomTo: seed.dateRangeCustomTo })}
        onCommit={(range) => onChange(range)}
        onClear={() => onChange({ dateRange: DROP_GEN_FIRST_RANGE, dateRangeCustomFrom: undefined, dateRangeCustomTo: undefined })}
      />
    </div>
  );
};
